import { Link } from "react-router-dom";
import { Phone, MapPin, Mail, MessageCircle, Linkedin, Instagram, Facebook, Youtube, FileDown } from "lucide-react";
import { company, whatsappUrl, buildQuoteUrl } from "@/config/site";

export default function Footer() {
  const year = new Date().getFullYear();
  const wa = whatsappUrl();
  const socials = [
    { key: "linkedin", href: company.linkedin, Icon: Linkedin, label: "LinkedIn" },
    { key: "instagram", href: company.instagram, Icon: Instagram, label: "Instagram" },
    { key: "facebook", href: company.facebook, Icon: Facebook, label: "Facebook" },
    { key: "youtube", href: company.youtube, Icon: Youtube, label: "YouTube" },
  ].filter((s) => !!s.href);

  return (
    <footer className="bg-neutral-900 text-neutral-300 mt-20" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid gap-10 md:grid-cols-4">
        <div className="md:col-span-2">
          <div className="flex items-start gap-4">
            <div className="bg-white p-2.5 rounded-sm">
              <img src={company.logo.src} alt={company.logo.alt} className="h-10 w-auto" />
            </div>
            <div>
              <div className="font-heading font-semibold text-white tracking-tight text-lg">
                {company.fullCompanyName}
              </div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 mt-0.5">
                Established {company.establishedYear}
              </div>
            </div>
          </div>
          <p className="mt-5 text-sm text-neutral-400 leading-relaxed max-w-md">
            Packaging solutions across corrugated, PP, protective packaging, accessories and pallets — supplied according to customer requirement.
          </p>
          <div className="mt-6 space-y-2.5 text-sm text-neutral-400">
            <div className="flex gap-3">
              <MapPin size={16} className="mt-0.5 text-neutral-500 shrink-0" aria-hidden="true" />
              <span>{company.addressFull}</span>
            </div>
            <a href={company.phoneHref} className="flex gap-3 hover:text-white" data-testid="footer-phone">
              <Phone size={16} className="mt-0.5 text-neutral-500" aria-hidden="true" />
              {company.phone}
            </a>
            {company.email ? (
              <a href={`mailto:${company.email}`} className="flex gap-3 hover:text-white" data-testid="footer-email">
                <Mail size={16} className="mt-0.5 text-neutral-500" aria-hidden="true" />
                {company.email}
              </a>
            ) : null}
            {wa ? (
              <a href={wa} target="_blank" rel="noopener noreferrer" className="flex gap-3 hover:text-white" data-testid="footer-whatsapp">
                <MessageCircle size={16} className="mt-0.5 text-neutral-500" aria-hidden="true" />
                Chat on WhatsApp
              </a>
            ) : null}
          </div>

          {(socials.length || company.brochureUrl) ? (
            <div className="mt-6 flex items-center gap-3">
              {socials.map(({ key, href, Icon, label }) => (
                <a key={key} href={href} target="_blank" rel="noopener noreferrer" aria-label={label} className="w-9 h-9 border border-neutral-700 flex items-center justify-center hover:border-white hover:text-white">
                  <Icon size={14} />
                </a>
              ))}
              {company.brochureUrl ? (
                <a href={company.brochureUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 border border-neutral-700 px-4 h-9 text-xs uppercase tracking-[0.16em] hover:border-white hover:text-white" data-testid="footer-brochure">
                  <FileDown size={14} /> Brochure
                </a>
              ) : null}
            </div>
          ) : null}
        </div>

        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-white mb-4">Company</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/" className="hover:text-white" data-testid="footer-link-home">Home</Link></li>
            <li><Link to="/about" className="hover:text-white" data-testid="footer-link-about">About</Link></li>
            <li><Link to="/products" className="hover:text-white" data-testid="footer-link-products">Products</Link></li>
            <li><Link to="/custom-packaging" className="hover:text-white">Custom Packaging</Link></li>
            <li><Link to="/industries" className="hover:text-white">Industries</Link></li>
            <li><Link to="/clients" className="hover:text-white">Clients</Link></li>
            <li><Link to="/contact" className="hover:text-white" data-testid="footer-link-contact">Contact</Link></li>
          </ul>
        </div>

        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-white mb-4">Resources</div>
          <ul className="space-y-2 text-sm">
            <li>
              <a href={buildQuoteUrl()} target="_blank" rel="noopener noreferrer" className="hover:text-white" data-testid="footer-link-quote">Request a Quote</a>
            </li>
            <li><Link to="/privacy-policy" className="hover:text-white" data-testid="footer-link-privacy">Privacy Policy</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-neutral-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 text-xs text-neutral-500 flex flex-col sm:flex-row justify-between gap-2">
          <div>© {year} {company.fullCompanyName}. All rights reserved.</div>
          <div className="tracking-wide">Greater Noida · Uttar Pradesh · India</div>
        </div>
      </div>
    </footer>
  );
}
